/**
 * ManualTopicDialog Component
 *
 * Dialog for creating manual topics with form validation.
 * Includes three fields: Topic title (required), Clarification (optional), URL with example (optional).
 */

import { useReducer, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2 } from 'lucide-react';
import { api } from '../../../convex/_generated/api';
import { IManualTopicDialogProps } from '../../@types/topics';

type FormState = {
  name: string;
  description: string;
  exampleUrl: string;
  isSubmitting: boolean;
};

type FormAction =
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_EXAMPLE_URL'; payload: string }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'RESET'; payload: FormState };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.payload };
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload };
    case 'SET_EXAMPLE_URL':
      return { ...state, exampleUrl: action.payload };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    case 'RESET':
      return action.payload;
  }
}

function getInitialFormState(
  mode: 'create' | 'edit',
  initialTopic: IManualTopicDialogProps['initialTopic']
): FormState {
  if (mode === 'edit' && initialTopic) {
    return {
      name: initialTopic.name,
      description: initialTopic.description || '',
      exampleUrl: initialTopic.exampleUrl || '',
      isSubmitting: false,
    };
  }
  return { name: '', description: '', exampleUrl: '', isSubmitting: false };
}

export const ManualTopicDialog = ({
  isOpen,
  onClose,
  onSuccess,
  mode = 'create',
  initialTopic,
}: IManualTopicDialogProps) => {
  const [formState, dispatch] = useReducer(
    formReducer,
    { mode, initialTopic },
    ({ mode: m, initialTopic: t }) => getInitialFormState(m, t)
  );
  const { name, description, exampleUrl, isSubmitting } = formState;

  const createManualTopic = useMutation(api.topics.createManualTopic);
  const updateManualTopic = useMutation(api.topics.updateManualTopic);

  const handleClose = useCallback(() => {
    dispatch({
      type: 'RESET',
      payload: { name: '', description: '', exampleUrl: '', isSubmitting: false },
    });
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!name.trim()) {
        toast.error('Topic title is required');
        return;
      }

      if (name.trim().length > 100) {
        toast.error('Topic title must be 100 characters or less');
        return;
      }

      dispatch({ type: 'SET_SUBMITTING', payload: true });

      try {
        let result;

        if (mode === 'edit' && initialTopic) {
          // Edit mode
          result = await updateManualTopic({
            topicId: initialTopic._id,
            name: name.trim(),
            description: description.trim() || undefined,
            exampleUrl: exampleUrl.trim() || undefined,
          });
        } else {
          // Create mode
          result = await createManualTopic({
            name: name.trim(),
            description: description.trim() || undefined,
            exampleUrl: exampleUrl.trim() || undefined,
          });
        }

        if (result.success) {
          toast.success(
            mode === 'edit' ? 'Topic updated successfully!' : 'Topic created successfully!'
          );
          handleClose();
          onSuccess();
        } else {
          toast.error(result.error || `Failed to ${mode === 'edit' ? 'update' : 'create'} topic`);
        }
      } catch {
        toast.error(
          `An error occurred while ${mode === 'edit' ? 'updating' : 'creating'} the topic`
        );
      } finally {
        dispatch({ type: 'SET_SUBMITTING', payload: false });
      }
    },
    [
      name,
      description,
      exampleUrl,
      mode,
      initialTopic,
      createManualTopic,
      updateManualTopic,
      handleClose,
      onSuccess,
    ]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Topic' : 'Add Manual Topic'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Update your topic details.'
              : 'Create a new topic for your content. You can generate posts from this topic later.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Topic Title */}
          <div className="space-y-2">
            <Label htmlFor="topic-title">
              Topic Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="topic-title"
              value={name}
              onChange={e => dispatch({ type: 'SET_NAME', payload: e.target.value })}
              placeholder="e.g., Social Media Marketing Tips"
              maxLength={100}
              disabled={isSubmitting}
              required
            />
            <p className="text-xs text-muted-foreground">{name.length}/100 characters</p>
          </div>

          {/* Clarification (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="clarification">Clarification (Optional)</Label>
            <Textarea
              id="clarification"
              value={description}
              onChange={e => dispatch({ type: 'SET_DESCRIPTION', payload: e.target.value })}
              placeholder="Additional details about this topic..."
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* URL with Example (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="example-url">URL with Example (Optional)</Label>
            <Input
              id="example-url"
              type="url"
              value={exampleUrl}
              onChange={e => dispatch({ type: 'SET_EXAMPLE_URL', payload: e.target.value })}
              placeholder="https://example.com/article"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground">Reference URL for inspiration</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {mode === 'edit' ? 'Updating...' : 'Saving...'}
                </>
              ) : mode === 'edit' ? (
                'Update'
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
